import { Alert, Button, InputNumber, Modal, Select, Typography } from 'antd'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { SspBean } from 'src/api/api'

const Option = Select.Option

const SHARE_COLUMN_WIDTH = 156
const EQUIVALENT_COLUMN_WIDTH = 104
const MAX_PERCENT = 100
const SMALL_TEXT: React.CSSProperties = { fontSize: '12px' }
const ELLIPSIS: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

// budget is a share of the campaign total budget, in percent
export interface SspBudgetEntry {
    sspId: number
    budget?: number
}

interface Props {
    ssps: SspBean[]
    value?: number[]
    onChange?: (value: number[]) => void
    budgets?: { [key: string]: number }
    totalBudget?: number
    onBudgetsChange?: (value?: { [key: string]: number }) => void
}

const precision = 2

const sumBudgets = (entries: SspBudgetEntry[]): number => {
    return Number(entries.reduce((acc, item) => acc + (item.budget ?? 0), 0).toFixed(precision))
}

const toMoneyEquivalent = (percent: number, totalBudget: number): string => {
    return (percent * totalBudget / MAX_PERCENT).toFixed(precision)
}

const normalizeBudgetsBySsp = (sspIds: number[], budgets?: { [key: string]: number }): SspBudgetEntry[] => {
    return sspIds.map((sspId) => ({
        sspId,
        budget: budgets ? budgets[sspId] : undefined,
    }))
}

const isSubset = (subset: number[], superset: number[]) => {
    return subset.every((item) => superset.includes(item))
}

const hasMoreThanTwoDecimals = (num: number) => {
    return Math.abs(num * 100 - Math.round(num * 100)) > 0.000001
}

const SspTrafficSelector: React.FC<Props> = ({ ssps, value = [], onChange, budgets, totalBudget, onBudgetsChange }) => {

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [draftBudgets, setDraftBudgets] = useState<SspBudgetEntry[]>([])
    const [error, setError] = useState<string>()

    const isModalDisabled = value.length <= 1

    const selectedSspMap = useMemo(() => new Map(ssps.map((ssp) => [ssp.id, ssp.name])), [ssps])

    const getSspLabel = useCallback((sspId: number) => selectedSspMap.get(sspId) || `SSP #${sspId}`, [selectedSspMap])

    const normalizedBudgets = useMemo(() => normalizeBudgetsBySsp(value, budgets), [value, budgets])

    const selectedBudgetEntries = useMemo(() => normalizedBudgets.filter((item) => item.budget !== undefined), [normalizedBudgets])

    const budgetSspIds = useMemo(() => Object.keys(budgets || {}).map((item) => Number(item)), [budgets])

    const summaryNameColumnWidth = useMemo(() => {
        const longestNameLength = normalizedBudgets.reduce((acc, item) => {
            const name = getSspLabel(item.sspId)
            return Math.max(acc, name.length)
        }, 0)

        const clampedLength = Math.max(12, Math.min(longestNameLength, 28))
        return `${clampedLength}ch`
    }, [getSspLabel, normalizedBudgets])

    const draftTotal = sumBudgets(draftBudgets)
    // total budget is used only to show the money equivalent of shares, not for validation
    const knownTotalBudget = typeof totalBudget === 'number' && !Number.isNaN(totalBudget) ? totalBudget : undefined
    const exceedsMaxPercent = draftTotal > MAX_PERCENT
    const hasAnyBudget = selectedBudgetEntries.length > 0
    const hasSelectionMismatch = budgetSspIds.length > 0 && !isSubset(budgetSspIds, value)

    const modalColumns = knownTotalBudget != null
        ? `1fr ${SHARE_COLUMN_WIDTH}px ${EQUIVALENT_COLUMN_WIDTH}px`
        : `1fr ${SHARE_COLUMN_WIDTH}px`

    const summaryColumns = knownTotalBudget != null
        ? `${summaryNameColumnWidth} 56px ${EQUIVALENT_COLUMN_WIDTH}px`
        : `${summaryNameColumnWidth} 56px`

    const onSelectChange = (selectedSspIds: number[]) => {
        onChange && onChange(selectedSspIds)

        if (selectedSspIds.length <= 1) {
            onBudgetsChange && onBudgetsChange({})
        }
    }

    const openModal = () => {
        if (isModalDisabled) return

        setDraftBudgets(normalizeBudgetsBySsp(value, budgets))
        setError(undefined)
        setIsModalOpen(true)
    }

    const onBudgetChange = (sspId: number, nextBudget?: number | null) => {
        setDraftBudgets((prev) => prev.map((item) => item.sspId === sspId ? { ...item, budget: nextBudget === null ? undefined : Number(nextBudget) } : item))
    }

    const onSave = () => {
        const hasInvalidValue = draftBudgets.some((item) => item.budget !== undefined && (item.budget < 0 || item.budget > MAX_PERCENT || hasMoreThanTwoDecimals(item.budget)))
        if (hasInvalidValue) {
            setError('Share should be between 0 and 100% and use at most 2 decimal places.')
            return
        }

        if (exceedsMaxPercent) {
            setError('Sum of reserved SSP shares cannot exceed 100%.')
            return
        }

        const nextBudgets = draftBudgets.reduce((acc, item) => {
            if (item.budget !== undefined) {
                acc[item.sspId] = item.budget
            }
            return acc
        }, {} as { [key: string]: number })

        onBudgetsChange && onBudgetsChange(Object.keys(nextBudgets).length === 0 ? {} : nextBudgets)
        setIsModalOpen(false)
    }

    const onClear = () => {
        onBudgetsChange && onBudgetsChange({})
        setIsModalOpen(false)
    }

    return <div>
        <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Select SSP"
            value={value}
            onChange={(selectedSspIds) => onSelectChange(selectedSspIds as number[])}
        >
            {ssps.map((item: SspBean) => <Option key={item.id} value={item.id as number}>{item.name}</Option>)}
        </Select>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ ...SMALL_TEXT, ...ELLIPSIS, color: '#cf1322', lineHeight: '18px', marginRight: '8px' }} />
            <Typography.Link
                onClick={openModal}
                style={{
                    ...SMALL_TEXT,
                    lineHeight: '18px',
                    color: isModalDisabled ? '#bfbfbf' : (isModalOpen ? '#859FC4' : '#1677ff'),
                    pointerEvents: isModalDisabled ? 'none' : 'auto',
                    cursor: isModalDisabled ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                }}
            >
                Set reserved SSP budget share
            </Typography.Link>
        </div>

        {value.length > 1 && hasAnyBudget && !hasSelectionMismatch && <div style={{ ...SMALL_TEXT, marginTop: '4px', color: '#8c8c8c', width: 'fit-content', maxWidth: '100%' }}>
            <div style={{ ...SMALL_TEXT, fontWeight: 500, marginBottom: '2px' }}>Reserved SSP budget share</div>
            {selectedBudgetEntries.map((item) => <div key={item.sspId} style={{
                display: 'grid',
                gridTemplateColumns: summaryColumns,
                gap: '8px',
                lineHeight: '18px',
                width: 'fit-content',
                maxWidth: '100%',
            }}>
                <span style={ELLIPSIS}>{getSspLabel(item.sspId)}</span>
                <span style={{ textAlign: 'right' }}>{item.budget}%</span>
                {knownTotalBudget != null && <span style={{ ...ELLIPSIS, textAlign: 'right' }}>≈ {toMoneyEquivalent(item.budget as number, knownTotalBudget)}$</span>}
            </div>)}
        </div>}

        <Modal
            centered
            open={isModalOpen}
            title="Set reserved SSP budget share"
            onCancel={() => setIsModalOpen(false)}
            width={720}
            onOk={onSave}
            okText="Save"
            cancelButtonProps={{ danger: true }}
            footer={[
                <Button key="save" type="primary" onClick={onSave}>
                    Save
                </Button>,
                <Button key="cancel" danger onClick={() => setIsModalOpen(false)}>
                    Cancel
                </Button>,
                <Button key="remove" onClick={onClear}>
                    Remove
                </Button>,
            ]}
        >
            {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} />}
            <div style={{ border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '6px' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: modalColumns,
                    alignItems: 'center',
                    columnGap: '10px',
                    background: '#fafafa',
                    color: '#8c8c8c',
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '10px 14px',
                }}>
                    <span>SSP</span>
                    <span>Share of Total Budget</span>
                    {knownTotalBudget != null && <span style={{ textAlign: 'right' }}>Amount</span>}
                </div>
                {draftBudgets.map((item) => <div key={item.sspId} style={{
                    display: 'grid',
                    gridTemplateColumns: modalColumns,
                    alignItems: 'center',
                    columnGap: '10px',
                    padding: '10px 14px',
                    borderTop: '1px solid #f5f5f5',
                }}>
                    <div style={{ ...ELLIPSIS, color: '#595959' }}>{getSspLabel(item.sspId)}</div>
                    <InputNumber
                        min={0}
                        max={MAX_PERCENT}
                        precision={precision}
                        step={0.01}
                        suffix="%"
                        style={{ width: `${SHARE_COLUMN_WIDTH}px` }}
                        value={item.budget}
                        onChange={(nextValue) => onBudgetChange(item.sspId, nextValue)}
                    />
                    {knownTotalBudget != null && <span style={{ ...SMALL_TEXT, ...ELLIPSIS, color: '#8c8c8c', textAlign: 'right' }}>
                        {item.budget !== undefined ? `≈ ${toMoneyEquivalent(item.budget, knownTotalBudget)}$` : '—'}
                    </span>}
                </div>)}
            </div>
            <div style={{ ...SMALL_TEXT, marginBottom: '22px', display: 'flex', gap: '8px', alignItems: 'center', color: '#8c8c8c', padding: '0 14px', flexWrap: 'wrap' }}>
                <span>Total</span>
                <span style={exceedsMaxPercent ? { color: '#cf1322', fontWeight: 600 } : { color: '#8c8c8c', fontWeight: 500 }}>{draftTotal.toFixed(2)}%</span>
                {knownTotalBudget != null && <span>≈ {toMoneyEquivalent(draftTotal, knownTotalBudget)}$ of {knownTotalBudget.toFixed(2)}$</span>}
                {exceedsMaxPercent && <span style={{ ...SMALL_TEXT, color: '#cf1322', fontWeight: 400 }}>cannot exceed 100%</span>}
            </div>
        </Modal>
    </div>
}

export default SspTrafficSelector



// WEBPACK FOOTER //
// ./src/component/app/campaign/SspTrafficSelector.tsx